import { getFeedsComments } from '@/pages/opinion/services';
import { getCommunityVideoMaxMetric } from '@op/services';
import React, { useEffect, useMemo } from 'react';
import { useTablePropsInfo } from '@/pages/opinion/hooks/common';
import TableCommonHook from '@op/components/TableCommonHook';
import { useTrendingColumn } from '@op/components/TableCommonHook/hooks/useTrendingColumn';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import { SortBy } from 'tea-component/lib/table/addons';

const videoSortByOptions = [
  { value: 'view', text: 'Views' },
  { value: 'engagement', text: 'Engagement' },
  { value: 'follower_number', text: 'Followers' },
  { value: 'tweets_reply', text: 'Comments' },
  { value: 'comment_time', text: 'Date' },
];

const videoSortColumns = videoSortByOptions.map((val) => val.value);

const videoExtraFilter = {
  review_types: ['posts_video', 'stream_titles'],
  media_types: ['video', 'live'],
};

export { videoSortByOptions };

export default function VideoTagTable({
  setSortBy,
  sortBy,
  filter,
  selectKeyword,
  backCommentTotal = (_value: number) => { },
  backLoading = (_value: boolean) => { },
  gameInfo,
  wholeGameInfo,
  setSortByOptions,
}: {
  setSortBy: (value: SortBy<any>) => void;
  sortBy: SortBy<any>;
  filter: any;
  selectKeyword: any;
  backCommentTotal?: (value: number) => void;
  backLoading?: (value: boolean) => void;
  gameInfo: any;
  wholeGameInfo: any;
  setSortByOptions: (value: { text: string; value: string }[]) => void;
}) {
  const {
    data,
    setData,
    pageInfo,
    changePageInfo,
    resetPageInfo,
    total,
    setTotal,
    loading,
    setLoading,
  } = useTablePropsInfo<any>({
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  const { wrapperRequestTile } = useCancelRequestHook({
    clear: () => {
      setData([]);
      setTotal(0);
    },
  });

  const { wrapperRequestTile: wrapperMaxMetric } = useCancelRequestHook();

  const [statistics, setStatistics] = React.useState<any>({});

  const setLoadingFunc = (value: boolean) => {
    setLoading(value);
    backLoading?.(value);
  };

  const fetchComments = async (params: any) => {
    setLoadingFunc(true);
    const requestList = [
      wrapperRequestTile(getFeedsComments, params),
      wrapperMaxMetric(getCommunityVideoMaxMetric, params),
    ];

    Promise.all(requestList)
      .then(([commentsData, maxMetricData = []]) => {
        const { list, total } = commentsData || {};
        setData(list || []);
        setTotal(total || 0);
        backCommentTotal(total || 0);
        if (maxMetricData?.length > 0) {
          setStatistics(maxMetricData[0] || {});
        }
      })
      .catch(() => { })
      .finally(() => {
        setLoadingFunc(false);
      });
  };

  const params = useMemo(() => ({
    ...filter,
    ...videoExtraFilter,
    sort_item: sortBy.by,
    sort_model: sortBy.order,
    keyword: selectKeyword,
    need_video_tags: true,
    ...pageInfo,
  }), [filter, sortBy, selectKeyword, pageInfo]);

  useEffect(() => {
    if (!filter?.edition_unified_id) return;
    fetchComments(params);
  }, [pageInfo]);

  useUpdateEffect(() => {
    resetPageInfo();
  }, [sortBy, filter, selectKeyword]);

  useEffect(() => {
    setSortByOptions(videoSortByOptions);
  }, []);

  const { columns } = useTrendingColumn({
    language: 'origin',
    wholeGameInfo,
    keys: ['video', 'authorCountry', 'view', 'engagement', 'subscribe', 'tweetsReply'],
    statistics,
    logBtnId: 'VideoTagDetail',
    gameInfo,
  });

  return (
    <TableCommonHook
      minWidth={1600}
      recordKey="comment_uin"
      records={data}
      tableSticky
      sortColumns={videoSortColumns}
      hasPageable
      sortInfo={[sortBy, setSortBy]}
      pageInfo={pageInfo}
      columns={columns}
      total={total}
      loading={loading}
      setPageInfo={changePageInfo}
    />
  );
}
